import React, { useEffect, useRef, useState } from 'react';
import InputMask from 'react-input-mask';
import {
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  InputGroup as ChakraInputGroup,
  InputLeftElement as ChakraInputLeftElement,
  InputRightElement as ChakraInputRightElement,
  useFormControlContext,
  forwardRef,
} from '@chakra-ui/react';
import {
  ColorMode,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import { AlertSolidIcon } from '@allergan-data-labs/alle-elements-icons/24/alertSolidIcon';

import {
  getAdditionalInputFieldStyles,
  getErrorIconStyles,
  getLeftElementStyles,
  getRightElementStyles,
} from './input.theme';

export interface InputProps extends ChakraInputProps {
  /**
   * @deprecated  Use `leftElement` instead.
   */
  leftIcon?: React.ReactNode;
  /**
   * @deprecated  Use `rightElement` instead.
   */
  rightIcon?: React.ReactNode;
  /**
   * If added, will show an element before the Input text
   */
  leftElement?: React.ReactNode;
  /**
   * If added, will show an element after the Input text
   */
  rightElement?: React.ReactNode;
  /**
   * @deprecated
   */
  isPostError?: boolean;
  /** 
    This prop determines which mode the component should display
    @default light
  */
  colorMode?: ColorMode;
  /**
   * Mask string. Format characters are:
   * * `9`: `0-9`
   * * `a`: `A-Z, a-z`
   * * `\*`: `A-Z, a-z, 0-9`
   *
   * Any character can be escaped with backslash, which usually will appear as double backslash in JS strings.
   *
   * For example, German phone mask with unremoveable prefix +49 will look like `mask="+4\\9 99 999 99"` or `mask={"+4\\\\9 99 999 99"}`
   *
   * @default /[\d\D]*\/
   *
   */
  mask?: string | Array<string | RegExp>;
  /**
   * Character to cover unfilled editable parts of mask.
   *
   * If set to `null`, unfilled parts will be empty, like in ordinary input.
   * @default null
   */
  maskChar?: string | null | undefined;

  /**
   * @description This is a prop to disable the mask input feature. This is useful when you want to use the input as a normal input field and naturally be integrated with third-party libraries, that leverages the default react behavior.
   * @default false
   */
  disableInputMask?: boolean;
  /**
   * The text shown inside the input prior to entry
   */
  placeHolder?: React.ReactNode;
  /**
   * Whether or not to display the default error icon when in an invalid state. If set to `false`, use `rightElement` to render your own icon when in an invalid state.
   * @default true
   */
  showErrorIcon?: boolean;
}

/** Input is used to get user input in a single-line text field. */
export const Input = forwardRef<InputProps, 'input'>((props, ref) => {
  const {
    leftElement,
    rightElement,
    leftIcon,
    rightIcon,
    isPostError,
    isInvalid: propIsInvalid,
    mask = /[\d\D]*/,
    maskChar = null,
    colorMode: propColorMode,
    onBlur = undefined,
    onMouseDown = undefined,
    disableInputMask = false,
    showErrorIcon = true,
    ...otherProps
  } = props;
  const { colorMode } = useColorMode(propColorMode);

  // Fallback to leftIcon/rightIcon if leftElement/rightElement are not provided
  const effectiveLeftElement = leftElement ?? leftIcon;
  const effectiveRightElement = rightElement ?? rightIcon;
  const leftElementRef = useRef<HTMLDivElement>(null);
  const rightElementRef = useRef<HTMLDivElement>(null);
  const [leftElementWidth, setLeftElementWidth] = useState<number>();
  const [rightElementWidth, setRightElementWidth] = useState<number>();
  const formControlContext = useFormControlContext();
  const [removeFocusBorder, setRemoveFocusBorder] = useState(false);
  const isInvalid = propIsInvalid || !!formControlContext?.isInvalid;

  const extraProps = !disableInputMask
    ? {
        inputRef: (el: HTMLInputElement) => {
          if (ref && 'current' in ref) {
            ref.current = el;
          }
        },
        mask,
        maskChar,
      }
    : { ref };

  useEffect(() => {
    setLeftElementWidth(leftElementRef?.current?.offsetWidth || 0);
    setRightElementWidth(rightElementRef?.current?.offsetWidth || 0);
  }, []);

  const ErrorIcon = (
    <AlertSolidIcon
      sx={getErrorIconStyles(colorMode)}
      data-testid="alert-icon"
    />
  );
  const additionalTextFieldStyles = getAdditionalInputFieldStyles({
    leftOffset: leftElementWidth,
    rightOffset: rightElementWidth,
    colorMode,
    removeFocusBorder,
  });

  const onMouseDownHandler = (event: React.MouseEvent<HTMLInputElement>) => {
    setRemoveFocusBorder(true);
    if (onMouseDown) {
      onMouseDown(event);
    }
  };

  const onBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
    setRemoveFocusBorder(false);
    if (onBlur) {
      onBlur(event);
    }
  };
  return (
    <ChakraInputGroup>
      {effectiveLeftElement && (
        <ChakraInputLeftElement
          ref={leftElementRef}
          pointerEvents="none"
          sx={getLeftElementStyles({ colorMode })}
        >
          {effectiveLeftElement}
        </ChakraInputLeftElement>
      )}
      <ChakraInput
        as={
          /**
           * This is a workaround to fix the issue with the `InputMask` component not working properly with the third party libs like `react-hook-form`.
           * The issue is that the `InputMask` component is not able to pass the ref to the input element, which is required by the third party libs to work properly.
           */
          (!disableInputMask && (InputMask as React.ElementType)) ||
          otherProps.as
        }
        {...extraProps}
        isInvalid={isInvalid}
        aria-invalid={isInvalid ? 'true' : 'false'}
        colorScheme={colorMode}
        sx={additionalTextFieldStyles}
        onBlur={onBlurHandler}
        onMouseDown={onMouseDownHandler}
        {...otherProps}
      />
      {(isInvalid ||
        otherProps['aria-invalid'] === 'true' ||
        effectiveRightElement) && (
        <ChakraInputRightElement
          ref={rightElementRef}
          sx={getRightElementStyles({ colorMode })}
        >
          {showErrorIcon && (isInvalid || otherProps['aria-invalid'] === 'true')
            ? ErrorIcon
            : effectiveRightElement}
        </ChakraInputRightElement>
      )}
    </ChakraInputGroup>
  );
});
